import { ActionModal, ActionModalProps, FormLayout } from '@kcftech/design-system';
import {
    InterestTypes,
    Persona,
    ProfileChild,
    ProfileOptionsResponse,
    UserProfileUpdateRequest,
} from '@kcftech/prometheus-client';
import { useMutation } from '@tanstack/react-query';
import { PersonaModalPage1 } from 'components/modal/persona/PersonaModalPage1';
import { PersonaModalPage2 } from 'components/modal/persona/PersonaModalPage2';
import { SpinnerArea } from 'components/spinner/SpinnerArea';
import { useNotificationContext } from 'contexts/NotificationContext';
import { usePermissions } from 'contexts/PermissionsContext';
import { useProfileSettingsOptions } from 'custom_hooks/data_hooks/user/useProfileSettingsOptions';
import { useCallback, useState } from 'react';
import { UseFormReturn, useForm } from 'react-hook-form';
import styled from 'styled-components';
import { updateUserProfile } from 'utils/user';

export type PersonaModalProps = Readonly<{
    profile: ProfileChild | undefined;
    onSubmit: () => void;
}>;

export type PersonaFormData = Readonly<{
    persona: Persona;
    customPersona?: string;
    personaInterests?: InterestTypes[];
}>;

export type PersonaModalPageProps = Readonly<{
    form: UseFormReturn<PersonaFormData>;
    options: ProfileOptionsResponse | undefined;
    selections: PersonaFormData;
}>;

const getPageInfo = ({ form, options, selections }: PersonaModalPageProps) => [
    {
        title: 'What is Your Primary Role?',
        actionButtonText: 'Next',
        content: (
            <PersonaModalPage1
                form={form}
                options={options}
                selection={selections.persona}
            />
        ),
    },
    {
        title: 'Areas of Interest',
        actionButtonText: 'Apply',
        content: (
            <PersonaModalPage2
                form={form}
                options={options}
                selection={selections.personaInterests}
            />
        ),
    },
];

export const personaIsOther = (persona: Persona | undefined) => persona === Persona.Other;

export function PersonaModal({ profile, onSubmit }: PersonaModalProps) {
    const { showToast } = useNotificationContext();
    const { canEditUserPersonas } = usePermissions();
    const { data: options, isLoading: optionsLoading } = useProfileSettingsOptions();
    const [pageIndex, setPageIndex] = useState<number>(0);

    const form = useForm<PersonaFormData>({
        mode: 'onSubmit',
        reValidateMode: 'onChange',
        defaultValues: {
            persona: profile?.Persona,
            customPersona: profile?.CustomPersona,
            personaInterests: profile?.PersonaInterests,
        },
    });
    const {
        watch,
        formState: { isValidating, isSubmitting, isSubmitted, isValid },
        handleSubmit,
        trigger,
    } = form;
    const {
        persona: selectedPersona,
        personaInterests: selectedPersonaInterests,
    } = watch();

    const verifyPage1 = useCallback(async () => {
        const isPage1Valid = await (personaIsOther(selectedPersona)
            ? trigger(['persona', 'customPersona'])
            : trigger('persona'));

        if (!isPage1Valid) {
            // need to trigger submit, so the onChange reValidateMode kicks in

            handleSubmit(() => {})();
        }

        return isPage1Valid;
    }, [handleSubmit, selectedPersona, trigger]);

    const pageInfo = getPageInfo({
        form,
        options,
        selections: {
            persona: selectedPersona,
            personaInterests: selectedPersonaInterests,
        },
    });
    const { title, actionButtonText, content } = pageInfo[pageIndex];

    const onClick = async () => {
        if (pageIndex === 0) {
            if (await verifyPage1()) {
                setPageIndex(1);
            }
        } else {
            handleSubmit((formData) => updatePersona(formData))();
        }
    };

    const { mutate: updatePersona, isPending } = useMutation({
        mutationFn: async (formData: PersonaFormData) => {
            if (!profile) {
                return;
            }
            const updates: Partial<UserProfileUpdateRequest> = {
                Persona: formData.persona,
                CustomPersona: personaIsOther(formData.persona)
                    ? formData.customPersona
                    : undefined,
                PersonaInterests: formData.personaInterests,
            };
            await updateUserProfile(profile.UserId, updates);
        },

        onSuccess: () => {
            onSubmit();
            showToast({
                type: 'success',
                content: 'Successfully set role and interests',
            });
            setPageIndex(0);
        },

        onError: () => {
            showToast({
                type: 'error',
                content: 'Failed to set role and interests',
            });
        },
    });

    if (!canEditUserPersonas) {
        return null;
    }

    return (
        <HackedModal
            title={title}
            onAction={onClick}
            actionButtonText={actionButtonText}
            actionable={
                !isPending && (!isSubmitted || isValid) && !(isValidating || isSubmitting)
            }
            showCloseButton={false}
            // design system does style computations using the `height` value,
            // which breaks for non-number values, thus current the hackery
            // below.
            //---
            // height="fit-content"
            footerContent={
                <div>
                    {pageIndex + 1} of {pageInfo.length}
                </div>
            }
        >
            <SpinnerArea isLoading={optionsLoading}>
                <FormLayout>{content}</FormLayout>
            </SpinnerArea>
        </HackedModal>
    );
}
// TODO @jp [SDWEB-255]: We don't have the ability to deploy
// `@kcftech/design-system` from GH yet, so I can't fix this in SD4 without
// modifying the package in AzDo (or otherwise bypassing CI and other publishing
// standards). This'd also later require an equivalent patch to GH, which is
// just a bunch of stuff I'd rather avoid. I (@jp) will take care of the GH
// deployment pipeline myself if not done by end of March, but others have been
// made responsible for that for now, we're simply in a period of migration
// pains.
function IntermediaryActionModal(props: ActionModalProps & { className?: string }) {
    return (
        <ActionModal {...props} windowClassName={props.className}>
            {props.children}
        </ActionModal>
    );
}

const HackedModal = styled(IntermediaryActionModal)`
    &[aria-modal='true'],
    & div[aria-modal='true'] {
        height: fit-content !important;
    }
`;
